import { ReactNode, useState, useMemo, SetStateAction, Dispatch } from 'react';
import { EntityState, createEntityAdapter } from '@reduxjs/toolkit';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import {
  Position,
  PositionView,
  processPositionView,
} from '#/positions/entities/positions';
import { Nullable } from '@paradigm/utils/types';
import {
  FetcherState,
  FetcherStatus,
} from '@paradigm/features/src/positions/contexts/abstractions/useFetcher';
import {
  SubscriptionState,
  SubscriptionStatus,
} from '@paradigm/features/src/positions/contexts/abstractions/useSubscriber';
import { usePositionsFetcherState } from './fetcher';
import { usePositionsSubscriberStatus } from './subscriber';

export interface PositionsState {
  readonly positions: EntityState<Position, string>;
  readonly lastUpdatedAt: Nullable<Date>;
}

export interface PositionsActions {
  readonly setAll: (
    positions: readonly Position[],
    lastUpdatedAt: Nullable<Date>,
  ) => void;
  readonly reset: () => void;
}

export interface PositionsView {
  readonly positions: PositionView[];
  readonly lastUpdatedAt: Nullable<Date>;
  readonly status: FetcherStatus;
  readonly error: Nullable<string>;
  readonly getAccountPositionByInstrumentId: (
    instrumentId: string,
    accountId: string,
  ) => Nullable<Position>;
}

const adapter = createEntityAdapter<Position>({});
const selectors = adapter.getSelectors();

const INITIAL_STATE = {
  positions: adapter.getInitialState(),
  lastUpdatedAt: null,
};

export const [usePositionsState, PositionsStateProvider] =
  createSafeContext<PositionsState>('PositionsState');
export const [usePositionsActions, PositionsActionsProvider] =
  createSafeContext<PositionsActions>('PositionsActions');
export const [usePositionsView, PositionsViewContext] =
  createSafeContext<PositionsView>('PositionsView');

interface Props {
  readonly children: ReactNode;
}

export function PositionsStoreProvider(props: Props) {
  const { children } = props;
  const [state, setState] = useState<PositionsState>(INITIAL_STATE);
  const actions = useMemo(() => createActions(setState), []);

  return (
    <PositionsActionsProvider value={actions}>
      <PositionsStateProvider value={state}>{children}</PositionsStateProvider>
    </PositionsActionsProvider>
  );
}

export function PositionsViewProvider(props: Props) {
  const { children } = props;
  const fetcherState = usePositionsFetcherState();
  const subscriberState = usePositionsSubscriberStatus();
  const state = usePositionsState();

  const view = useMemo(
    () => createView(state, fetcherState, subscriberState),
    [state, fetcherState, subscriberState],
  );

  return <PositionsViewContext value={view}>{children}</PositionsViewContext>;
}

function createView(
  state: PositionsState,
  fetcherState: FetcherState,
  subscriberState: SubscriptionState,
) {
  return {
    positions: selectors.selectAll(state.positions).map(processPositionView),
    lastUpdatedAt: state.lastUpdatedAt,
    status: createViewStatus(fetcherState.status, subscriberState.status),
    error: subscriberState.error || fetcherState.error,
    getAccountPositionByInstrumentId(instrumentId: string, accountId: string) {
      return (
        selectors
          .selectAll(state.positions)
          .find(
            (pos) =>
              pos.instrument_name === instrumentId &&
              pos.account_name === accountId,
          ) ?? null
      );
    },
  };
}

function createViewStatus(
  fetcherStatus: FetcherStatus,
  subscriberStatus: SubscriptionStatus,
) {
  return subscriberStatus === 'SUBSCRIBED'
    ? fetcherStatus
    : subscriberStatus === 'ERROR'
    ? 'ERROR'
    : 'LOADING';
}

function createActions(
  setState: Dispatch<SetStateAction<PositionsState>>,
): PositionsActions {
  return {
    setAll(positions, newLastUpdatedAt) {
      setState((state) => ({
        ...state,
        positions: adapter.setAll(state.positions, positions),
        lastUpdatedAt: newLastUpdatedAt,
      }));
    },
    reset() {
      setState(INITIAL_STATE);
    },
  };
}
